import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText } from "lucide-react";
import {
  BulkActionBar,
  Button,
  EmptyState,
  Input,
  Page,
  Select,
  Table,
  Text,
  type Column,
  type MenuItem,
} from "@marekl/adminer/components";
import { notifySuccess } from "@marekl/adminer/api";
import { useConfirm, useDebouncedSearchFilter, useRowSelection, useOptionalFilters } from "@marekl/adminer/hooks";
import { useSetTopbarTitle } from "@marekl/adminer/shell";
import { useCurrentCompany } from "../../lib/company";
import {
  copyPath,
  documentPath,
  useConvertDocument,
  useDeleteDocument,
  useDocumentList,
  type DocumentStatus,
  type DocumentSummary,
  type DocumentType,
} from "../../lib/queries/documents";
import { usePagedList } from "../../lib/usePagedList";
import { formatMoney } from "../../lib/money";
import { formatCivil, todayCivil } from "../../lib/dates";
import { relativeCivil } from "../../lib/relative";
import { printDocument, useDocumentPdf } from "../../lib/documentActions";
import { ListCard } from "../../components/ListCard";
import { RowActions } from "../../components/RowActions";
import styles from "./DocumentsPage.module.css";

/** Legacy titles (legacy/app/src/resources/Receipts.tsx getTitle). */
export function docTitles(type: DocumentType, vatPayer: boolean) {
  switch (type) {
    case "retail":
      return { main: "Prodejky", list: "Seznam prodejek", create: "Nová prodejka", edit: "Úprava prodejky" };
    case "purchase_invoice":
      return { main: "Přijatá faktura", list: "Seznam přijatých faktur", create: "Nová přijatá faktura", edit: "Úprava přijaté faktury" };
    case "sales_order":
      return { main: "Cenová nabídka", list: "Seznam cenových nabídek", create: "Nová cenová nabídka", edit: "Úprava cenové nabídky" };
    case "credit_note":
      return vatPayer
        ? { main: "Dobropis", list: "Seznam dobropisů", create: "Nový dobropis", edit: "Úprava dobropisu" }
        : { main: "Storno faktura", list: "Seznam storno faktur", create: "Nová storno faktura", edit: "Úprava storno faktury" };
    default:
      return { main: "Vydaná faktura", list: "Seznam vydaných faktur", create: "Nová vydaná faktura", edit: "Úprava vydané faktury" };
  }
}

/** Total of the selected rows, per currency ("Celkem: 1 234,00 Kč"). */
function selectionTotal(rows: DocumentSummary[]): string {
  const by = new Map<string, number>();
  for (const r of rows) by.set(r.currency, (by.get(r.currency) ?? 0) + r.total_incl_vat);
  return [...by].map(([cur, v]) => formatMoney(v, cur)).join(" + ");
}

/** One list per document type (route /c/:companyId/<slug>), as the legacy list. */
export function DocumentsPage({ type }: { type: DocumentType }) {
  const { id, can, company } = useCurrentCompany();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const status = (searchParams.get("status") ?? "") as DocumentStatus | "";
  const year = searchParams.get("year") ?? "";
  const q = searchParams.get("q") ?? "";
  const purchase = type === "purchase_invoice";
  const titles = docTitles(type, company.billing_details.vat_payer);
  useSetTopbarTitle(titles.main);

  const list = usePagedList(
    useDocumentList(id),
    { type, status: status || undefined, year: year ? Number(year) : undefined, q: q || undefined },
    { searchParams, setSearchParams, nounOf: ["dokladu", "dokladů"] },
  );
  const { draft, setDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => list.filterReset((p) => (next ? p.set("q", next) : p.delete("q"))),
  });
  const setFilter = (key: string, value: string) => list.filterReset((p) => (value ? p.set(key, value) : p.delete(key)));
  const thisYear = Number(todayCivil().slice(0, 4));
  const rows = list.data?.items ?? [];
  const sel = useRowSelection(rows, (d) => d.id);
  const selected = rows.filter((r) => sel.selection.selectedKeys.has(r.id));
  const del = useDeleteDocument(id);
  const convert = useConvertDocument(id);
  const pdf = useDocumentPdf(id);
  const confirm = useConfirm();
  const editor = can("editor");

  async function deleteDrafts(ids: string[]) {
    if (!(await confirm({ title: "Smazat koncepty", description: `Smazat ${ids.length} rozpracovaných dokladů?`, confirmLabel: "Smazat", variant: "danger" }))) return;
    for (const d of ids) await del.mutateAsync(d);
    sel.clear();
    notifySuccess("Koncepty byly smazány.");
  }

  function rowActions(d: DocumentSummary): MenuItem[] {
    const items: MenuItem[] = [];
    if (editor && d.status === "issued" && type === "sales_order") {
      items.push({
        id: "invoice",
        label: "vyfakturovat",
        onSelect: async () => {
          const inv = await convert.mutateAsync(d.id);
          navigate(`${documentPath(id, inv.id)}/edit`);
        },
      });
    }
    if (editor && d.status === "issued" && type === "sales_invoice") {
      items.push({ id: "credit", label: company.billing_details.vat_payer ? "dobropis" : "storno", onSelect: () => navigate(`${documentPath(id, d.id)}?credit=1`) });
    }
    if (editor && type !== "credit_note") {
      items.push({ id: "copy", label: "kopírovat", onSelect: () => navigate(copyPath(id, d.id)) });
    }
    items.push({ id: "pdf", label: "PDF", onSelect: () => void pdf.download(d.id) });
    items.push({ id: "print", label: "Tisk", onSelect: () => printDocument(id, d.id) });
    if (editor && d.status === "draft") {
      items.push({ type: "separator" }, { id: "delete", label: "Smazat", danger: true, onSelect: () => void deleteDrafts([d.id]) });
    }
    return items;
  }

  const columns: Column<DocumentSummary>[] = [
    {
      key: "number",
      header: "Číslo",
      primary: true,
      render: (d) => (d.number ? <span className={styles.number}>{d.number}</span> : <span className={styles.draft}>koncept</span>),
      linkTo: (d) => documentPath(id, d.id),
    },
    {
      key: "party",
      header: purchase ? "Dodavatel" : "Odběratel",
      render: (d) => (purchase ? d.supplier_name : d.customer_name) || "—",
    },
    { key: "issue", header: type === "retail" ? "Datum" : "Vystaveno", align: "center", render: (d) => formatCivil(d.issue_date) },
    ...(type === "sales_invoice" || type === "purchase_invoice"
      ? [
          {
            key: "due",
            header: "Splatnost",
            align: "right" as const,
            render: (d: DocumentSummary) =>
              d.due_date ? (
                // Legacy DueDateField: due today or earlier in red, still to
                // come in amber — both bold, with the relative day under it.
                <span className={`${styles.due} ${d.due_date <= todayCivil() ? styles.overdue : styles.upcoming}`}>
                  {formatCivil(d.due_date)}
                  <span className={styles.rel}>{relativeCivil(d.due_date)}</span>
                </span>
              ) : (
                "—"
              ),
          },
        ]
      : []),
    {
      key: "total",
      header: company.billing_details.vat_payer ? "Cena s DPH" : "Cena",
      numeric: true,
      render: (d) => <span className={styles.total}>{formatMoney(d.total_incl_vat, d.currency)}</span>,
    },
    {
      key: "actions",
      header: "",
      render: (d) => <RowActions items={rowActions(d)} to={documentPath(id, d.id)} label={`dokladu ${d.number ?? "koncept"}`} />,
    },
  ];

  const filters = useOptionalFilters({
    filters: [
      {
        key: "q",
        label: "Číslo nebo název",
        alwaysOn: true,
        wide: true,
        active: !!q,
        onClear: () => setFilter("q", ""),
        render: () => <Input label="Číslo nebo název" value={draft} onChange={(e) => setDraft(e.target.value)} />,
      },
      {
        key: "status",
        label: "Stav",
        active: !!status,
        onClear: () => setFilter("status", ""),
        render: () => (
          <Select label="Stav" value={status} onChange={(e) => setFilter("status", e.target.value)}>
            <option value="">Vše</option>
            <option value="draft">Koncepty</option>
            <option value="issued">Vystavené</option>
          </Select>
        ),
      },
      {
        key: "year",
        label: "Rok",
        active: !!year,
        onClear: () => setFilter("year", ""),
        render: () => (
          <Select label="Rok" value={year} onChange={(e) => setFilter("year", e.target.value)}>
            <option value="">Všechny</option>
            {Array.from({ length: 8 }, (_, i) => thisYear - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
        ),
      },
    ],
  });

  return (
    <Page>
      <Page.Content>
        <ListCard
          title={titles.list}
          onCreate={editor && type !== "credit_note" ? () => navigate(`/c/${id}/documents/new?type=${type}`) : undefined}
          filters={filters}
          pagination={list.pagination}
          summary={list.summary}
        >
          <Table<DocumentSummary>
            cards
            columns={columns}
            rows={rows}
            rowKey={(d) => d.id}
            selection={{ ...sel.selection, label: (d) => d.number ?? "koncept" }}
            isLoading={list.isLoading}
            error={list.error}
            onRetry={list.refetch}
            empty={<EmptyState icon={FileText} title="Žádné doklady" description={q || status || year ? "Zkuste upravit filtr." : undefined} />}
          />
        </ListCard>
        <BulkActionBar count={sel.selectedCount} onClear={sel.clear} nounForms={["doklad", "doklady", "dokladů"]}>
          <Text variant="number">Celkem: {selectionTotal(selected)}</Text>
          {editor && selected.length > 0 && selected.every((d) => d.status === "draft") && (
            <Button size="sm" variant="danger" onClick={() => void deleteDrafts(selected.map((d) => d.id))}>
              Smazat koncepty
            </Button>
          )}
        </BulkActionBar>
      </Page.Content>
    </Page>
  );
}
